import "react-circular-progressbar/dist/styles.css";

import { useQuery, WatchQueryFetchPolicy } from "@apollo/client";
import { useAtom } from "jotai";
import dynamic from "next/dynamic";
import { Italiana } from "next/font/google";
import Head from "next/head";
import { useRouter } from "next/router";
import { NextSeo } from "next-seo";
import React, { useEffect, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { buildStyles, CircularProgressbar } from "react-circular-progressbar";
import InfiniteScroll from "react-infinite-scroller";

import Button from "@/components/core/button";
import Loading from "@/components/core/loading";
import Header from "@/components/ui/header";
import ProductList from "@/components/ui/product-list";
import ReturnToButton from "@/components/ui/return-top-button";
import SearchBar from "@/components/ui/search-bar";
import SearchModal from "@/components/ui/search-pattern/modal";
import PRESEARCH from "@/graphql/queries/presearch";
import SEARCH from "@/graphql/queries/search";
import {
  Curation as CurationType,
  PresearchQuery,
  Search_Item_FieldsFragment,
  Search_Type,
  SearchQuery,
} from "@/graphql/types/graphql";
import useMe from "@/hooks/useMe";
import useRequestId from "@/hooks/useRequestId";
import useTracker, { preferenceOriginAtom } from "@/hooks/useTracker";
import { useIsMobile } from "@/utils/breakPoints";
import { decodeURL, encodeURL } from "@/utils/index";
import { searchResultRoute } from "@/utils/search";
import { createStructuredData } from "@/utils/structuredData";

const Curation = dynamic(() => import("@/components/page/results/curation"), {
  ssr: true,
});

const CategoryLandingInfo = dynamic(
  () => import("@/components/page/results/category-landing-info"),
  {
    ssr: true,
  }
);

const Switch = dynamic(
  () => import("@nextui-org/switch").then((mod) => mod.Switch),
  { ssr: false }
);

const ProgressProvider = dynamic(
  () => import("@/components/ui/progress-provider"),
  { ssr: false }
);

export const FIRST = 40;

const italiana = Italiana({
  subsets: ["latin"],
  weight: "400",
});

interface ResultsProps {
  search: string;
  requestId?: string;
  preData?: SearchQuery["search"];
  viewEvent: any;
  fetchPolicy?: WatchQueryFetchPolicy;
  isSSG?: boolean;
  inMySize?: boolean;
}

const Results = ({
  search,
  preData,
  requestId = "",
  viewEvent,
  fetchPolicy,
  isSSG = false,
  inMySize = false,
}: ResultsProps) => {
  const metadata = {
    title: `${search} | Plush`,
    description: `Explore ${search} from top brands on Plush - the curated and personalized AI fashion search platform.`,
    canonical: `https://www.plush.shop/${
      isSSG ? "edits" : "results"
    }/${encodeURL(search)}`,
    ogTitle: `${search}, Plush`,
    ogType: "website",
    image: "https://www.plush.shop/social-post.png",
    siteName: "Plush",
  };

  const router = useRouter();
  const query = decodeURL(router.query.search as string);
  const type = router.query.type as string;
  const [searchValue, setSearchValue] = useState(query);
  const returnTopRef = useRef<HTMLDivElement>(null);
  const searchbarRef = useRef<HTMLInputElement | null>(null);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const { searchEvent, rerunEvent } = useTracker();
  const [viewTracked, setTracked] = useState(false);
  const { goLogin, me, isPreferenceSet } = useMe();
  const { setRequestId } = useRequestId();
  const [isRetry, setIsRetry] = useState<boolean>(false);
  const [localRequestId, setLocalRequestId] = useState<string>(requestId);
  const [searchbarLoading, setSearchbarLoading] = useState<boolean>(false);
  const [refetchLoading, setRefetchLoading] = useState<boolean>(false);
  const [showSizeSetTips, setShowSizeSetTips] = useState(false);
  const [ssgLoading, setSsgLoading] = useState(false);
  const [, setPreferenceOrigin] = useAtom(preferenceOriginAtom);
  const [curationLoadingIdx, setCurationLoadingIdx] = useState(0);
  const isMobile = useIsMobile();

  const [isInMySize, setIsInMySize] = useState(
    inMySize ||
      (!isSSG &&
        typeof window !== "undefined" &&
        sessionStorage.getItem("inMySize") === "TRUE")
  );
  const [isFirstRender, setIsFirstRender] = useState(true);

  const handleToPersonalize = () => {
    setPreferenceOrigin("popup");
    router.push("/preference?redirectURL=" + location.pathname + "&inMySize=1");
    setShowSizeSetTips(false);
  };

  const { data: presearchData } = useQuery<PresearchQuery>(PRESEARCH, {
    variables: {
      query: query,
    },
    skip: !query,
    errorPolicy: "all",
  });

  const { data, loading, fetchMore, error, refetch } = useQuery<SearchQuery>(
    SEARCH,
    {
      variables: {
        query: query,
        first: FIRST,
        useOwnSize: isInMySize,
        skipPersonalization: isSSG && !isInMySize,
        retry: false,
        type,
      },
      skip: !query,
      errorPolicy: "all",
      fetchPolicy: fetchPolicy || "cache-first",
    }
  );

  const searchType = React.useMemo(() => {
    return presearchData?.presearch?.type || "";
  }, [presearchData]);

  const validData = React.useMemo(() => {
    return data?.search || preData;
  }, [data, preData]);

  const items = React.useMemo(() => {
    return validData?.items;
  }, [validData]);

  const categoryLandingInfo = React.useMemo(() => {
    return validData?.categoryLandingInfo;
  }, [validData]);

  const itemsEdges = React.useMemo(() => {
    return items?.edges || [];
  }, [items]);

  const itemsNodes = React.useMemo(() => {
    return itemsEdges.map((result) => {
      return result.node;
    });
  }, [itemsEdges]);

  const partial = React.useMemo(() => {
    return validData?.partial;
  }, [validData]);

  const hasNextPage = React.useMemo(() => {
    return items?.pageInfo?.hasNextPage;
  }, [items]);

  const explanation = React.useMemo(() => {
    return items?.explanation || "";
  }, [items]);

  const curation = React.useMemo(() => {
    return validData?.curation;
  }, [validData]);

  const pageLoading = loading || refetchLoading;

  useEffect(() => {
    if (window?.sessionStorage.getItem("inMySize") && !isSSG) {
      setIsInMySize(true);
    }
  }, []);

  const handleIsMySizeChange = async () => {
    if (isSSG && !ssgLoading && isInMySize) {
      setSsgLoading(true);
    }
    if (isInMySize) {
      window?.sessionStorage.setItem("inMySize", "TRUE");
      document.cookie = `in_my_size=TRUE; path=/; secure; samesite=strict`;
    } else {
      window.sessionStorage.removeItem("inMySize");
      document.cookie = "in_my_size=; path=/; max-age=0";
    }

    if (!isFirstRender) {
      setRefetchLoading(true);
      await refetch({
        useOwnSize: isInMySize,
        skipPersonalization: isSSG && !isInMySize,
      });
      setRefetchLoading(false);
    } else {
      setIsFirstRender(false);
    }
  };

  useEffect(() => {
    handleIsMySizeChange();
  }, [isInMySize]);

  const loadMore = async () => {
    if (!hasNextPage) return;
    if (!query) return;

    fetchMore({
      variables: {
        query: query,
        first: FIRST,
        after:
          itemsEdges && itemsEdges.length !== 0
            ? itemsEdges[itemsEdges.length - 1].cursor
            : undefined,
        id: localRequestId,
        skipPersonalization: isSSG && !isInMySize,
        useOwnSize: isInMySize,
        retry: isRetry,
        type: Search_Type.Item,
      },
    });
  };

  const handleRerunSearch = async () => {
    setTracked(false);
    setIsRetry(true);
    setRefetchLoading(true);
    await refetch({ retry: true });
    // if (isRetry) {
    //   await refetch();
    // }
    rerunEvent({ query });
    setRefetchLoading(false);
  };

  const handleNewSearch = () => {
    setSearchValue("");
    if (searchbarRef && searchbarRef.current) {
      searchbarRef.current.focus();
    }
  };

  const onSearchModalClose = () => {
    setIsSearchModalOpen(false);
  };

  useEffect(() => {
    setSearchValue(query);
  }, [query]);

  // Edit Search
  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  const handleFormSubmit = async (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    event.preventDefault();
    if (!searchValue) {
      return;
    }
    if (router) {
      searchEvent({
        query: searchValue,
        smart_filter_content: "",
        original_query: searchValue,
      });

      if (searchValue === query) {
        router.reload();
      } else {
        setSearchbarLoading(true);
        await router.push(searchResultRoute(searchValue), undefined, {
          shallow: true,
        });
        setSearchbarLoading(false);
      }
    }
  };

  const searchBarValue = Array.isArray(searchValue)
    ? searchValue[0]
    : searchValue || "";

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  const handleScroll = () => {
    if (returnTopRef && returnTopRef.current) {
      const scrollTopButton = returnTopRef.current;

      scrollTopButton.style.display = window.scrollY > 200 ? "block" : "none"; // 页面滚动超过200px时显示按钮
    }
  };

  // Scroll to Top
  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (isSSG && requestId) {
      setRequestId(requestId);
      setLocalRequestId(requestId);
    }
  }, []);

  useEffect(() => {
    if (!pageLoading && data && !viewTracked) {
      setLocalRequestId(data.search?.items?.searchID || "");
      setRequestId(data.search?.items?.searchID || "");
    }
  }, [pageLoading]);

  useEffect(() => {
    if (!viewTracked && localRequestId) {
      const error_type =
        data?.search?.result === "INVALID_INPUT"
          ? "NOT_SUPPORTED"
          : data?.search?.items?.edges?.length === 0
          ? "ZERO_RESULTS"
          : null;

      viewEvent({
        query,
        error_type,
        request_id: localRequestId,
      });

      setTracked(true);
    }
  }, [localRequestId]);

  useEffect(() => {
    // SSG need to refetch search query to update any info needs auth, like "saved".
    if (isSSG) refetch({ id: requestId });
  }, []);

  const productClickHandler = (node: Search_Item_FieldsFragment) => {
    router.push(`/product${node.seoURL}`);
  };

  const curationLoading = [
    {
      text: "Interpreting your request...",
      start: 0,
      end: 10,
      duration: 2,
    },
    {
      text: "Researching the latest trends...",
      start: 11,
      end: 25,
      duration: 3,
    },
    {
      text: "Curating style advice...",
      start: 26,
      end: 55,
      duration: 4,
    },
    {
      text: "Gathering outfit inspo...",
      start: 56,
      end: 80,
      duration: 3,
    },
    {
      text: "Pulling shoppable products for you...",
      start: 81,
      end: 99,
      duration: 4,
    },
  ];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            createStructuredData({
              description: metadata.description,
              url: metadata.canonical,
            })
          ),
        }}
      />
      <NextSeo
        title={metadata.title}
        description={metadata.description}
        canonical={metadata.canonical}
        openGraph={{
          title: metadata.ogTitle,
          description: metadata.description,
          url: metadata.canonical,
          images: [{ url: metadata.image, alt: "Plush" }],
          siteName: metadata.siteName,
          type: metadata.ogType,
        }}
      />
      <Head>
        <title>{`${query} | Plush`}</title>
      </Head>
      <InfiniteScroll
        hasMore={!!hasNextPage}
        loadMore={loadMore}
        loader={undefined}
        threshold={isMobile ? 700 : 500}
      >
        <main className="result-page flex flex-col items-center min-h-screen bg-white relative z-10">
          <div className="cover"></div>
          <Header position="relative" />
          {searchType && searchType !== Search_Type.Curation && (
            <SearchModal
              show={isSearchModalOpen}
              onHide={onSearchModalClose}
              initSearch={searchValue}
              onNewSearch={handleNewSearch}
            />
          )}
          <div className="section-container bg-white search_input_postion">
            <div id="searchBar">
              <SearchBar
                value={searchBarValue}
                position="relative"
                onChange={handleSearchChange}
                onSubmit={handleFormSubmit}
                defaultSingleRow="true"
                ref={searchbarRef}
                isLoading={searchbarLoading}
              />
            </div>
            {(!loading || isSSG) && (
              <div
                className={`totalCount_style flex justify-between`}
                style={{ color: "#191919", zIndex: 1 }}
              >
                <span className="rerun" onClick={() => handleRerunSearch()}>
                  <img className="rerun-icon" src="/reload.svg" />
                  <span className="rerun-text">Rerun</span>
                </span>

                <div className="flex gap-[12px]">
                  <Switch
                    isSelected={isInMySize}
                    onValueChange={(val) => {
                      if (!me) {
                        router.push(
                          "/login?redirectURL=" +
                            location.pathname +
                            "&inMySize=true"
                        );
                        return;
                      }
                      if (!val) {
                        setIsInMySize(false);
                        return;
                      }
                      if (isPreferenceSet) {
                        setIsInMySize(val);
                        return;
                      }
                      setShowSizeSetTips(true);
                    }}
                    classNames={{
                      base: "custom-switch-base",
                      wrapper: "custom-switch-wrapper",
                      thumb: "custom-switch-thumb",
                      // thumbIcon: "custom-switch-thumbIcon",
                    }}
                  >
                    <span className="in-my-size-text">My Size</span>
                  </Switch>
                  <Modal
                    show={showSizeSetTips}
                    centered
                    onHide={() => setShowSizeSetTips(false)}
                    style={{ zIndex: 999999 }}
                  >
                    <div className="modal-content">
                      <div className={`${italiana.className} modal-title`}>
                        Oops! We Don’t Know
                        <br />
                        Your Sizes Yet.
                      </div>
                      <div className="modal-section">
                        <p className="section-title">
                          Tell us your preferences now to get a more
                          personalized experience!
                        </p>
                      </div>
                      <div className="modal-actions">
                        <Button
                          style={{
                            border: "1px solid #E7E7E7",
                            backgroundColor: "#fff",
                            color: "#191919",
                          }}
                          onClick={() => setShowSizeSetTips(false)}
                        >
                          Maybe Later
                        </Button>
                        <Button onClick={handleToPersonalize}>
                          Personalize
                        </Button>
                      </div>
                    </div>
                  </Modal>

                  {!curation && (
                    <span
                      className="smart-filters"
                      onClick={() => setIsSearchModalOpen(true)}
                    >
                      <img className="smart-filters-icon" src="/options.svg" />
                      <span className="smart-filters-text">Filters</span>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
          {/* SSG page doesn't need loading for first render */}
          {pageLoading && (!isSSG || ssgLoading || isRetry) ? (
            searchType === "" ? (
              <div
                className={`section-container flex flex-col items-center min-h-screen loading-screen`}
              ></div>
            ) : searchType === Search_Type.Curation ? (
              <div
                className={`section-container flex flex-col items-center min-h-screen loading-screen`}
                key={curationLoadingIdx}
              >
                <ProgressProvider
                  valueStart={curationLoading[curationLoadingIdx].start}
                  valueEnd={curationLoading[curationLoadingIdx].end}
                  duration={curationLoading[curationLoadingIdx].duration}
                  onFinish={() => {
                    if (curationLoadingIdx < curationLoading.length - 1)
                      setCurationLoadingIdx((prev) => prev + 1);
                  }}
                >
                  {(value) => (
                    <>
                      <div className={`${italiana.className} loading-progress`}>
                        <CircularProgressbar
                          value={value}
                          text={`${value}%`}
                          styles={buildStyles({
                            textColor: "#191919",
                            pathColor: "#191919",
                            trailColor: "#F3F3F3",
                            textSize: "36px",
                            pathTransitionDuration: 0.5,
                            pathTransition: "easeQuadInOut",
                          })}
                          strokeWidth={3}
                        />
                      </div>
                      <div
                        className={italiana.className + " search_result_text"}
                        style={{
                          color: "#191919",
                          marginTop: 20,
                          paddingLeft: 32,
                          paddingRight: 32,
                        }}
                      >
                        {curationLoading[curationLoadingIdx].text}
                      </div>
                    </>
                  )}
                </ProgressProvider>
              </div>
            ) : (
              <div
                className={`section-container flex flex-col items-center min-h-screen loading-screen`}
              >
                <div className={`${italiana.className} loading-progress`}>
                  <ProgressProvider valueStart={0} valueEnd={99} duration={0.3}>
                    {(value) => (
                      <CircularProgressbar
                        value={value}
                        text={`${value}%`}
                        styles={buildStyles({
                          textColor: "#191919",
                          pathColor: "#191919",
                          trailColor: "#F3F3F3",
                          textSize: "36px",
                          pathTransitionDuration: 0.5,
                          pathTransition: "easeQuadInOut",
                        })}
                        strokeWidth={3}
                      />
                    )}
                  </ProgressProvider>
                </div>
                <div
                  className={italiana.className + " search_result_text"}
                  style={{
                    color: "#191919",
                    marginTop: 20,
                    paddingLeft: 32,
                    paddingRight: 32,
                  }}
                >
                  Finding your perfect look...
                </div>
              </div>
            )
          ) : error ? (
            <div className="no-results-container">
              <div className={`${italiana.className} search_result_text`}>
                An error has occurred
              </div>
              <div className="search_result_text_subtitle">
                Please try to rerun the search or refresh the page
              </div>
              <div className="flex">
                <div className="no-results-button" onClick={handleRerunSearch}>
                  <img src="/reload-white.svg" style={{ marginRight: 11 }} />
                  <span>Rerun search</span>
                </div>
              </div>
            </div>
          ) : (
            <>
              {curation && (
                <div className="section-container page-section curation-wrapper ">
                  <Curation curation={curation as CurationType} />
                </div>
              )}
              {itemsNodes.length === 0 ? (
                <>
                  {!curation && (
                    <div className="no-results-container">
                      <div
                        className={`${italiana.className} search_result_text`}
                      >
                        No results found
                      </div>
                      <div className="search_result_text_subtitle">
                        {data?.search?.result === "INVALID_INPUT"
                          ? "Oops! We currently only support women's apparel."
                          : "We couldn't find what you're looking for... yet!"}
                      </div>
                      <div className="search_result_text_subtitle">
                        {!me ? (
                          <>
                            If you think this was a mistake and should&apos;ve
                            returned a result, try running the search again.
                            Otherwise, sign up and we&apos;ll let you know as
                            soon as we expand our selection!
                          </>
                        ) : (
                          <>
                            If you think this was a mistake and should&apos;ve
                            returned a result, try running the search again.
                            Otherwise, we&apos;ll send you an email as soon as
                            we expand our selection!
                          </>
                        )}
                      </div>
                      <div className="flex">
                        <div
                          className="no-results-button"
                          onClick={handleRerunSearch}
                        >
                          <img
                            src="/reload-white.svg"
                            style={{ marginRight: 11 }}
                          />
                          <span>Rerun search</span>
                        </div>
                        {!me && (
                          <div
                            className="no-results-button white"
                            onClick={() => goLogin()}
                          >
                            Sign up
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <React.Fragment>
                  {partial && (
                    <div className="section-container partial_match ">
                      <div className="flex flex-row relative partial_match_inner">
                        <div className="partial_match_icon shrink-0">
                          <img
                            src="/partial-icon.svg"
                            style={{ width: "24px", height: "24px" }}
                          ></img>
                        </div>
                        <div className="partial_match_text shrink">
                          We couldn&apos;t find the perfect match, but here are
                          some partial matches:
                        </div>
                      </div>
                    </div>
                  )}
                  {explanation && !curation && (
                    <div className="section-container explanation-wrapper">
                      <div className="explanation">
                        {isSSG && <h1 className="edits-header">{query}</h1>}
                        {explanation}
                      </div>
                    </div>
                  )}
                  {categoryLandingInfo && (
                    <div className="section-container page-section">
                      <CategoryLandingInfo
                        categoryLanding={categoryLandingInfo}
                      />
                    </div>
                  )}
                  <div className="section-container goods_over_style">
                    {itemsNodes && (
                      <div className="section-container">
                        {curation && (
                          <div className={`items-title ${italiana.className}`}>
                            Shop More
                          </div>
                        )}
                        <ProductList
                          items={itemsNodes}
                          onClickProduct={productClickHandler}
                        />
                      </div>
                    )}
                  </div>

                  {/* 回到顶部 */}
                  <ReturnToButton />
                </React.Fragment>
              )}
            </>
          )}
          {hasNextPage && itemsNodes && itemsNodes.length > 0 && (
            <div className="page-loading">
              <Loading />
            </div>
          )}
          <style jsx>{`
            .in-my-size-text {
              color: #191919;
              text-align: center;
              font-size: 14px;
              font-style: normal;
              font-weight: 700;
              line-height: normal;
              letter-spacing: -0.28px;
            }
            .in-my-size-text::before {
              content: "In ";
              @media (max-width: 480px) {
                content: "";
              }
            }
            .result-page {
              .page-section {
                padding: 0 72px;

                @media (max-width: 768px) {
                  padding: 0 24px;
                }
              }
              :global(.custom-switch-wrapper) {
                box-sizing: border-box;
                width: 21px;
                height: 12px;
                padding-left: 2px !important;
                padding-right: 2px !important;
                border-radius: 100px;
                background: #898a8d;
                :global(.custom-switch-thumb) {
                  margin-left: 1px;
                  width: 8px;
                  height: 8px;
                  background: #ffffff !important;
                }
              }
              :global(.group[data-selected="true"] .custom-switch-wrapper) {
                background: #48742c !important;
                :global(.custom-switch-thumb) {
                  margin-left: 8px;
                  width: 8px;
                  height: 8px;
                  background: #ffffff !important;
                }
              }
            }

            .modal-content {
              padding: 60px 40px 40px;

              @media (max-width: 480px) {
                padding: 32px 24px;
              }

              .modal-title {
                color: #191919;
                font-size: 36px;
                font-style: normal;
                font-weight: 400;
                line-height: 42.41px;
                text-align: center;

                @media (max-width: 480px) {
                  font-size: 24px;
                }
              }
              .modal-section {
                margin-top: 40px;

                @media (max-width: 480px) {
                  margin-top: 24px;
                }

                .section-title {
                  color: #666;
                  font-size: 16px;
                  line-height: 19.2px;
                  font-style: normal;
                  font-weight: 400;
                  margin-bottom: 16px;

                  text-align: center;

                  @media (max-width: 480px) {
                    margin-top: 0;
                  }
                }
              }

              .modal-actions {
                margin-top: 32px;
                display: grid;
                grid-template-columns: 1fr 1fr;
                column-gap: 16px;

                @media (max-width: 480px) {
                  margin-top: 24px;
                }
              }
            }

            .page-loading {
              padding-bottom: 56px;
            }
            .page-section {
              padding: 0 72px;
            }
            .total-count-number {
              color: #191919;
              font-size: 14px;
              font-style: normal;
              font-weight: 500;
              line-height: normal;
            }
            .loading-screen {
              margin-top: 24px;
              justify-content: center;
            }
            .search_input_postion {
              position: sticky;
              padding: 32px 72px 24px;
              z-index: 10;
              top: 0;
            }
            .partial_match {
              padding: 0 72px;
              margin-bottom: 40px;
              align-items: flex-start;
            }

            .partial_match_inner {
              padding: 16px;
              border: 1px solid #e6e6e6;
              background-color: #f7f7f7;
            }
            .partial_match_text {
              font-size: 16px;
              font-weight: 500;
              color: #191919;
            }

            .partial_match_icon {
              align-self: center;
              margin-right: 12px;
            }

            .goods_over_style {
              width: 100%;
              display: flex;
              flex-wrap: wrap;
              padding: 0 72px;
              margin-top: 16px;
            }

            .goods-item-span {
              margin-bottom: 56px;
              margin-right: 2.6%;
              width: 23%;
              cursor: pointer;

              &:nth-child(4n) {
                margin-right: 0;
              }
            }

            .totalCount_style {
              margin-top: 24px;
            }

            .modalshow {
              display: block;
            }
            .modalHide {
              display: none;
            }

            .returnToTop_class {
              display: block;
              position: fixed;
              right: 24px;
              bottom: 24px;
              width: 40px;
              height: 40px;
              display: none;
              cursor: pointer;
            }

            .search_result_text {
              text-align: center;
              font-size: 64px;
              color: #191919;
              margin-bottom: 16px;
            }

            .search_result_text_subtitle {
              text-align: center;
              font-size: 16px;
              font-weight: 500;
              line-height: 24px; /* 150% */
              color: #666666;
              margin-bottom: 30px;
              max-width: 687px;
            }

            .loading-progress {
              width: 120px;
              height: 120px;
              margin-top: -350px;
            }

            .explanation-wrapper {
              padding: 0 72px;
              margin: 8px 0 24px;
            }

            .edits-header {
              font-size: 20px;
              font-weight: 500;
              color: #000;
              margin-bottom: 8px;
            }

            .explanation {
              padding: 12px 16px;
              border: 1px solid #e6e6e6;
              background-color: #f7f7f7;
              color: #666;
              font-size: 14px;
              font-style: normal;
              font-weight: 500;
              line-height: 20px; /* 142.857% */
            }

            .no-results-container {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              padding: 32px 32px;
              text-align: center;
              min-height: 50vh;
            }
            .rerun {
              display: flex;
              align-items: center;
              cursor: pointer;
              .rerun-icon {
                width: 12px;
                height: 12px;
                margin-right: 8px;
              }
              .rerun-text {
                color: #191919;
                font-size: 14px;
                font-style: normal;
                font-weight: 700;
                line-height: normal;
                letter-spacing: -0.28px;
                &::after {
                  content: " Search";
                  @media (max-width: 480px) {
                    content: "";
                  }
                }
              }
            }

            .smart-filters {
              display: flex;
              align-items: center;
              cursor: pointer;
              .smart-filters-icon {
                width: 16px;
                height: 16px;
                margin-right: 8px;
              }
              .smart-filters-text {
                color: #191919;
                font-size: 14px;
                font-style: normal;
                font-weight: 700;
                line-height: normal;
                &::before {
                  content: "Smart ";
                  @media (max-width: 480px) {
                    content: "";
                  }
                }
              }
            }

            .no-results-button {
              width: 218px;
              background: #191919;
              color: #ffffff;
              padding: 11px;
              display: flex;
              align-items: center;
              justify-content: center;
              margin: 0 8px;
              cursor: pointer;

              text-align: center;
              font-size: 16px;
              font-style: normal;
              font-weight: 700;
              line-height: normal;
              letter-spacing: -0.28px;
              -webkit-font-smoothing: antialiased;
              -moz-osx-font-smoothing: grayscale;
              font-smooth: never;

              &.white {
                border: 1px solid #191919;
                color: #191919;
                background: #fff;
              }
            }
            .items-title {
              color: #191919;
              font-size: 36px;
              font-style: normal;
              font-weight: 400;
              line-height: normal;
              margin-bottom: 40px;
            }
            @media (max-width: 768px) {
              .page-loading {
                padding-bottom: 24px;
              }
              .page-section {
                padding: 0 24px;
              }
              .search_result_text {
                font-size: 38px;
              }
              .search_result_text_subtitle {
                font-size: 14px;
              }

              .partial_match {
                padding: 0 24px;
                margin-bottom: 24px;
              }

              .goods_over_style {
                width: 100%;
                margin: 0;
                padding-left: 24px;
                padding-right: 24px;
                justify-content: space-between;
              }
              .goods-item-span {
                width: calc(50% - 8px);
                margin-left: 0;
                margin-right: 0;
                margin-bottom: 24px;
              }
              .cover {
                position: fixed;
                width: 100%;
                background-color: #fff;
                z-index: 50;
                height: 32px;
                top: 0;
                left: 0;
              }
              .search_input_postion {
                width: 100%;
                padding-left: 24px;
                padding-right: 24px;
                margin-top: 0px;
                padding-bottom: 24px;
                padding-top: 0px;
                top: 32px;
              }

              .explanation-wrapper {
                padding: 0 24px;
                margin: 0 0 24px;
              }
              .loading-screen {
                justify-content: flex-start;
              }
              .loading-progress {
                width: 76px;
                height: 76px;
                margin-top: 100px;
              }
              .no-results-button {
                width: 180px;
              }
            }
          `}</style>
          <style global jsx>{`
            .results-search-bar {
              @media (max-width: 768px) {
                height: 56px;
              }
            }
          `}</style>
        </main>
      </InfiniteScroll>
    </>
  );
};

export default Results;
